import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useUserState } from '~/atoms/UserState'
import { SALESFORCE_CHAT } from '~/constants/support'
import { usePartnerConfig } from '~/hooks/useCurrentPartner'
import { router } from '~/router'
import Logger from '~/services/Logger'
import {
  getAppVersion,
  isElectron,
  isLocalBuildEnvironment,
} from '~/utils/common'

import { isSalesforceSDKError } from './salesforceSdkErrors'

const logger = new Logger('SupportChatWidget')

/**
 * Route prefixes where the support chat launcher is hidden. The launcher is
 * offered on every other page. The Texting (Messaging) page is excluded
 * because the launcher button could overlap and conflict with the messaging
 * UI controls.
 */
const HIDDEN_ROUTE_PREFIXES = ['/messaging']

const isSupportChatHiddenPath = (pathname: string): boolean =>
  HIDDEN_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )

const SUPPORT_CHAT_BODY_ATTR = 'data-support-chat-route'

/**
 * Subscribes to the data router so the widget can react to navigation even
 * though it renders OUTSIDE the `<RouterProvider>` tree (mounted as a sibling
 * in `app.tsx`), where `useLocation` is unavailable.
 */
const useShowSupportChat = (): boolean =>
  useSyncExternalStore(
    (onChange) => router.subscribe(() => onChange()),
    () => !isSupportChatHiddenPath(router.state.location.pathname),
  )

/**
 * Salesforce Embedded Messaging globals exposed by the bootstrap script.
 *
 * Only the surface we actually call is typed here — the full API is
 * documented at:
 * https://developer.salesforce.com/docs/service/messaging-web/guide/sdk-introduction.html
 */
type EmbeddedServiceBootstrap = {
  settings?: {
    language?: string
    [key: string]: unknown
  }
  init: (
    orgId: string,
    deploymentDeveloperName: string,
    siteUrl: string,
    options: { scrt2URL: string },
  ) => void
  prechatAPI?: {
    setHiddenPrechatFields: (fields: Record<string, string>) => void
  }
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedServiceBootstrap
    __mangoSalesforceChatLoaded?: boolean
  }
}

const SCRIPT_ID = 'mango-salesforce-chat-bootstrap'

/**
 * Resolves once the Salesforce SDK has fired its `onEmbeddedMessagingReady`
 * event (or immediately, if the event already fired earlier in this page
 * lifetime). After this resolves, `embeddedservice_bootstrap.prechatAPI` is
 * guaranteed to be available, which is required before calling
 * `setHiddenPrechatFields`.
 *
 * The Salesforce SDK populates `prechatAPI` asynchronously, so calling
 * `setHiddenPrechatFields` synchronously after `bootstrap.init()` silently
 * no-ops on the first page load. See:
 * https://developer.salesforce.com/docs/service/messaging-web/guide/event-listeners.html
 *
 * Module-scoped (vs. per-mount) so we register exactly one window listener
 * per page lifetime, regardless of how many times the widget mounts.
 */
let salesforceReady: Promise<void> | null = null
const waitForSalesforceReady = (): Promise<void> => {
  if (salesforceReady) return salesforceReady
  if (typeof window === 'undefined') return Promise.resolve()
  salesforceReady = new Promise<void>((resolve) => {
    window.addEventListener('onEmbeddedMessagingReady', () => resolve(), {
      once: true,
    })
  })
  return salesforceReady
}

/**
 * Build the hidden pre-chat fields sent to Salesforce.
 *
 * IMPORTANT: these are HINTS, not credentials. The chat is unauthenticated
 * (matching legacy-admin behavior), so any user can edit these values in
 * DevTools before the chat is created. Support agents should continue to
 * verify identity via the Support PIN or other out-of-band means rather
 * than trusting these fields.
 *
 * The right-hand-side keys MUST match the API names of hidden pre-chat
 * fields configured on the Mango_Chat deployment in Salesforce. Confirm
 * with the Salesforce admin.
 */
const buildHiddenFields = (args: {
  sessionUser: NonNullable<ReturnType<typeof useUserState>['sessionUser']>
  pbx: NonNullable<ReturnType<typeof useUserState>['pbx']>
  userRoles?: ReturnType<typeof useUserState>['userRoles']
  partnerKey?: string
  appVersion: string
}): Record<string, string> => {
  const { sessionUser, pbx, userRoles, partnerKey, appVersion } = args
  const fields: Record<string, string> = {}

  if (sessionUser.id) fields.UserId = String(sessionUser.id)
  if (sessionUser.username) fields.Username = sessionUser.username
  if (sessionUser.email) fields.UserEmail = sessionUser.email
  if (sessionUser.first_name) fields.FirstName = sessionUser.first_name
  if (sessionUser.last_name) fields.LastName = sessionUser.last_name

  if (pbx.id) fields.PbxId = String(pbx.id)
  if (pbx.uuid) fields.PbxUuid = pbx.uuid
  if (pbx.name) fields.PbxName = pbx.name
  if (pbx.billing_info?.plan_name) fields.MangoPlan = pbx.billing_info.plan_name

  if (partnerKey) fields.Partner = partnerKey

  const roleNames = userRoles?.roles?.map((r) => r.name).filter(Boolean)
  if (roleNames && roleNames.length > 0) {
    fields.UserRoles = roleNames.join(', ')
  }
  if (userRoles?.is_staff) fields.IsStaff = 'true'
  if (userRoles?.is_superuser) fields.IsSuperuser = 'true'

  fields.Channel = isElectron ? 'electron' : 'web'
  if (appVersion) fields.AppVersion = appVersion
  fields.PageUrl = window.location.href

  return fields
}

/**
 * Mounts the Salesforce Service Cloud Embedded Messaging widget.
 *
 * Behavior matches the legacy-admin implementation in
 * `application/views/layout.php`: an anonymous chat session with hidden
 * pre-chat fields populated from the logged-in user's session for agent
 * context. The fields are unverified hints — see `buildHiddenFields`.
 *
 * Mounted globally in `app.tsx` (as a sibling of `<RouterProvider>`, hence
 * the `router.subscribe`-based route tracking rather than `useLocation`). The
 * launcher is shown on routes where the chat is offered (see
 * `HIDDEN_ROUTE_PREFIXES`); the SDK is injected the first time the user
 * reaches an eligible route, and the launcher button is shown/hidden via a
 * `<body>` attribute + CSS. The hook below also no-ops until `sessionUser` and
 * `pbx` are populated, so unauthenticated routes never inject the script.
 *
 * The bootstrap script is injected once per page load. Remounting (e.g.
 * due to React StrictMode or route changes) is a no-op. Salesforce does
 * not provide a teardown API for the launcher UI, so once loaded it remains
 * in the DOM until the next full navigation; we hide the launcher with CSS
 * rather than removing it, and an already-open conversation is left visible
 * even on hidden routes (see `app.global.css`).
 *
 * TODO(support-chat): Consider enabling Salesforce user verification (JWT)
 * if claim spoofing of pre-chat fields ever becomes a concern. That
 * requires a mango-api endpoint to mint signed tokens and Salesforce admin
 * configuration for the signing key.
 */
export const SupportChatWidget = () => {
  const { sessionUser, pbx, userRoles } = useUserState()
  const partnerConfig = usePartnerConfig()
  const showSupportChat = useShowSupportChat()
  const scriptInjected = useRef(false)

  // Suppress unhandled errors thrown by the Salesforce SDK's async internals.
  // These errors (e.g., reading 'showChatButton' from undefined) originate
  // from the SDK's bootstrap script after init() returns and are not
  // actionable from our code.
  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      if (isSalesforceSDKError(event)) {
        logger.warn('Suppressed Salesforce SDK error', {
          message: event.message,
        })
        event.preventDefault()
      }
    }
    window.addEventListener('error', handler)
    return () => window.removeEventListener('error', handler)
  }, [])

  // Reflect the current route on <body> so CSS can show/hide the launcher
  // (see `app.global.css`). An in-progress conversation stays visible
  // regardless; this only governs the launcher button.
  useEffect(() => {
    if (showSupportChat) {
      document.body.setAttribute(SUPPORT_CHAT_BODY_ATTR, 'visible')
    } else {
      document.body.removeAttribute(SUPPORT_CHAT_BODY_ATTR)
    }
  }, [showSupportChat])

  useEffect(() => {
    if (!sessionUser || !pbx) return
    // The Embedded Messaging deployment only allowlists real Mango origins, so
    // on localhost the SDK's config fetch rejects with an uncaught
    // "Unable to load Embedded Messaging configuration." promise error. Skip
    // injection there — the widget can't function anyway, and loading it only
    // produces noise (and fails Playwright's runtime-error smoke assertions).
    if (isLocalBuildEnvironment()) return
    // Only inject/init the Salesforce SDK on routes where the launcher is
    // offered. The SDK has no teardown API, so there's no reason to load it on
    // hidden routes. Once injected it persists for the page lifetime; the
    // body-attribute + CSS gate above hides the launcher again on hidden
    // routes.
    if (!showSupportChat) return

    let cancelled = false

    const applyHiddenFields = async () => {
      try {
        const appVersion = await getAppVersion().catch(() => '')
        if (cancelled) return
        const hiddenFields = buildHiddenFields({
          sessionUser,
          pbx,
          userRoles,
          partnerKey: partnerConfig.partnerKey,
          appVersion,
        })
        if (Object.keys(hiddenFields).length === 0) return
        // Wait for `onEmbeddedMessagingReady` so `prechatAPI` is populated.
        // Resolves immediately on subsequent renders (e.g. PBX switch) since
        // the SDK only fires this event once per page lifetime.
        await waitForSalesforceReady()
        if (cancelled) return
        const bootstrap = window.embeddedservice_bootstrap
        if (!bootstrap?.prechatAPI?.setHiddenPrechatFields) return
        bootstrap.prechatAPI.setHiddenPrechatFields(hiddenFields)
      } catch (err) {
        logger.error(
          'Error setting Salesforce chat pre-chat fields',
          err as Error,
        )
      }
    }

    const initBootstrap = () => {
      if (cancelled) return
      const bootstrap = window.embeddedservice_bootstrap
      if (!bootstrap) {
        logger.warn('embeddedservice_bootstrap not available after script load')
        return
      }
      // Register the ready listener BEFORE `bootstrap.init()` so we don't
      // miss the `onEmbeddedMessagingReady` event if it fires synchronously
      // during init.
      void waitForSalesforceReady()
      try {
        if (bootstrap.settings) {
          bootstrap.settings.language = SALESFORCE_CHAT.language
        }
        bootstrap.init(
          SALESFORCE_CHAT.orgId,
          SALESFORCE_CHAT.deploymentDeveloperName,
          SALESFORCE_CHAT.siteUrl,
          { scrt2URL: SALESFORCE_CHAT.scrt2Url },
        )
      } catch (err) {
        logger.error(
          'Error initializing Salesforce Embedded Messaging',
          err as Error,
        )
      }
      void applyHiddenFields()
    }

    // Script already loaded earlier in this page lifetime (e.g. user
    // navigated away from and back to an eligible route, or switched PBX).
    // Re-apply pre-chat fields so support agents see the current user/PBX
    // context.
    if (
      window.__mangoSalesforceChatLoaded &&
      window.embeddedservice_bootstrap
    ) {
      void applyHiddenFields()
      return () => {
        cancelled = true
      }
    }

    // A previous run on this mount injected the <script> tag but it
    // hasn't fired `load` yet. The existing `load` listener will call
    // initBootstrap (which calls applyHiddenFields with the latest deps
    // closed over by that listener) — nothing to do here.
    if (scriptInjected.current) {
      return () => {
        cancelled = true
      }
    }

    const existing = document.getElementById(
      SCRIPT_ID,
    ) as HTMLScriptElement | null
    if (existing) {
      scriptInjected.current = true
      existing.addEventListener('load', initBootstrap, { once: true })
      return () => {
        cancelled = true
      }
    }

    scriptInjected.current = true
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SALESFORCE_CHAT.bootstrapScript
    script.async = true
    script.addEventListener('load', () => {
      window.__mangoSalesforceChatLoaded = true
      initBootstrap()
    })
    script.addEventListener('error', () => {
      logger.warn('Failed to load Salesforce Embedded Messaging script')
    })
    document.body.appendChild(script)

    return () => {
      cancelled = true
    }
  }, [sessionUser, pbx, userRoles, partnerConfig.partnerKey, showSupportChat])

  return null
}

export default SupportChatWidget
